/**
 * Form Builder page walkthrough (IsfPageTour).
 * Targets must match data-tour attributes on ESourceTemplateBuilder.jsx.
 *
 * @param {object} context
 * @param {boolean} [context.isNew]
 * @param {boolean} [context.isReadOnly]
 * @param {boolean} [context.hasFields]
 * @param {(tool: string) => void} [context.setLeftTool]
 */
export function buildEsourceFormBuilderTourSteps(context = {}) {
  const {
    isNew = true,
    isReadOnly = false,
    hasFields = false,
    setLeftTool,
  } = context;

  const goLibrary = () => {
    try {
      if (isNew) setLeftTool?.("library");
      else setLeftTool?.("details");
    } catch {
      /* tour */
    }
  };
  const goDetails = () => {
    try {
      setLeftTool?.("details");
    } catch {
      /* tour */
    }
  };

  const goAssist = () => {
    try {
      setLeftTool?.("assist");
    } catch {
      /* tour */
    }
  };

  const steps = [
    {
      id: "welcome",
      placement: "center",
      title: "Welcome to Form Builder",
      body: isReadOnly
        ? "You are viewing a published form (preview only). To change it, go back to Form Management and create a new draft version. Use Guide anytime for a refresher."
        : isNew
          ? "Design electronic source forms here: start from Proto Types, AQ Assist (including PDF/Word upload), or field types. Add fields on the canvas, preview, then save a draft. Publish later from Form Management. Reopen this walkthrough from Guide."
          : "Edit the draft on the canvas, use Form details or AQ Assist on the left (chat or attach a PDF/Word file), then Update to save. Publish from Form Management when ready. Reopen this walkthrough from Guide.",
    },
    {
      id: "guide-button",
      target: '[data-tour="esource-fb-guide"]',
      placement: "bottom",
      title: "Page guide",
      body: "Click Guide to replay this Form Builder walkthrough whenever you need it.",
    },
    {
      id: "proto-types",
      target: '[data-tour="esource-fb-proto-tab"]',
      placement: "right",
      title: "Proto Types",
      body: "Starter layouts by form type (for example Pharmacy/ePSF or Laboratory). Pick a proto type to load a ready field set onto the canvas, then edit labels and options.",
      when: () => isNew && !isReadOnly,
      onEnter: goLibrary,
    },
    {
      id: "form-details",
      target: '[data-tour="esource-fb-details-tab"]',
      placement: "right",
      title: "Form details",
      body: "See form code, name, type, and version summary while you edit. Switch to AQ Assist when you want help changing this form or importing a document.",
      when: () => !isNew && !isReadOnly,
      onEnter: goDetails,
    },
    {
      id: "aq-assist",
      target: '[data-tour="esource-fb-assist-tab"]',
      placement: "right",
      title: "AQ Assist",
      body: "Describe the form or a change in plain English (for example “vital signs form” or “add a divider after the 6th field”), or attach a PDF/Word file with (+) then press Send. Assist maps sections, Yes/No, checklists and sign-off rows onto the canvas when it can. Daily usage follows your organisation’s AQ Assist limit. Always review before saving. (Your Super Admin must enable AQ Assist for Form Builder.)",
      when: () => !isReadOnly,
      onEnter: goAssist,
    },
    {
      id: "aq-assist-attach",
      target: '[data-tour="esource-fb-assist-attach"]',
      placement: "top",
      title: "Attach PDF or Word",
      body: "Use (+) to attach a .pdf, .doc or .docx form (tables and multi-page files are supported). The file appears as a chip — it does not generate until you press Send. Review every field Assist creates before you save.",
      when: () => !isReadOnly,
      onEnter: goAssist,
    },
    {
      id: "canvas",
      target: '[data-tour="esource-fb-canvas"]',
      placement: "left",
      title: "Form canvas",
      body: hasFields
        ? "Fields appear here in order. Drag the grip to reorder. Use Settings (cog) for labels, required, help text, validation, and conditional logic. Duplicate or remove fields as needed."
        : "The canvas is empty. Load a Proto Type, ask AQ Assist (or attach a PDF/Word file), or drag field types from the right. This is what people will fill later.",
    },
    {
      id: "form-settings",
      target: '[data-tour="esource-fb-form-settings"]',
      placement: "bottom",
      title: "Form settings",
      body: "Optional form header and description, plus one-column or two-column layout. These appear above the fields in preview and when the form is filled.",
      when: () => !isReadOnly,
    },
    {
      id: "field-types",
      target: '[data-tour="esource-fb-field-types"]',
      placement: "left",
      title: "Field types",
      body: "Drag or click to add Basic, Choice, Date & time, Structure (repeating table), and Layout items (section, divider, spacer). Build the form piece by piece after a proto type, Assist import, or from scratch.",
      when: () => !isReadOnly,
    },
    {
      id: "field-settings",
      placement: "center",
      title: "Field settings & logic",
      body: "Open Settings on a field to set label, field name, required, help text, width, options, and validation. Use Conditional logic to show, hide, enable, or disable fields based on other answers.",
      when: () => !isReadOnly,
    },
    {
      id: "preview",
      target: '[data-tour="esource-fb-preview"]',
      placement: "bottom",
      title: "Preview",
      body: "Try the form as a filler would see it, including required checks and conditional logic. Preview does not publish the form.",
    },
    {
      id: "save",
      target: '[data-tour="esource-fb-save"]',
      placement: "bottom",
      title: isNew ? "Save draft" : "Update",
      body: isNew
        ? "Save creates the form with a code, name, and form type. You can keep editing the draft. When the design is ready, publish from Form Management."
        : "Update saves changes to the current draft version. Publish from Form Management to make this design the live version.",
      when: () => !isReadOnly,
    },
    {
      id: "publish-note",
      placement: "center",
      title: "Publishing",
      body: "Publishing is done from Form Management (Publish on the form row). After publish, this version is read-only. Create a new draft version from Form Management to make further changes.",
    },
    {
      id: "done",
      placement: "center",
      title: "You are ready",
      body: "Proto Types, AQ Assist (chat or PDF/Word upload), or Field types to start; Preview to check; Save draft / Update to keep work; then Publish from Form Management. Use Guide anytime for a refresher.",
    },
  ];

  return steps.filter((step) => (step.when ? step.when(context) : true));
}
